import { useCallback, useEffect, useRef, useState } from 'react';
import { useData } from '@hooks/useData';
import { useRefreshToken } from '@services/useRefreshToken';

// refresh session every 5 minutes
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const useTokenRefreshLoop = () => {
    const { login } = useData();
    const { request: requestRefreshToken } = useRefreshToken();

    const [loading, setLoading] = useState(false);

    const isLoggedIn = login !== null;
    const shouldRefreshOnMount = useRef<boolean>(true);

    const requestRefreshTokenRef = useRef(requestRefreshToken);
    requestRefreshTokenRef.current = requestRefreshToken;

    // initial call to refresh endpoint
    const screenBlockingTokenRefresh = useCallback(async () => {
        setLoading(true);

        try {
            await requestRefreshTokenRef.current();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn) {
            shouldRefreshOnMount.current = false;
            return;
        }

        if (shouldRefreshOnMount.current) {
            shouldRefreshOnMount.current = false;
            screenBlockingTokenRefresh();
        }

        let cancelled = false;

        const run = async () => {
            if (cancelled) return;
            await requestRefreshTokenRef.current();
            if (cancelled) return;
            setTimeout(run, REFRESH_INTERVAL_MS);
        };

        const timer = setTimeout(run, REFRESH_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [isLoggedIn, screenBlockingTokenRefresh]);

    return { loading };
};
